import { useEffect, useState } from "react";
import { Fil } from "../components/Fil";

interface Props {
  titre: string;
  lignes: readonly string[];
}

/**
 * Ce qu'on annonce à la personne, et sur quoi la barre est calée.
 *
 * Trente secondes, et non vingt-cinq : les recettes de déploiement mesurent
 * l'appel réel entre vingt-deux et trente-sept secondes. Une barre calée sur
 * une durée plus courte que la vraie finit toujours par mentir.
 */
const ATTENTE_ANNONCEE_MS = 30000;

/**
 * Écran d'analyse.
 *
 * La barre **n'atteint jamais son terme et ne se fige jamais** : elle avance
 * d'une fraction de ce qui reste, donc de moins en moins vite, sans s'arrêter.
 * Le réglage précédent — sept points toutes les demi-secondes, plafonnés à
 * quatre-vingt-quatorze — atteignait le plafond en **6,8 secondes**, puis restait
 * immobile une quinzaine de secondes : le signal le plus sûr, pour un visiteur,
 * qu'une page a planté. La durée réelle mesurée en production est de vingt à
 * vingt-cinq secondes.
 *
 * `prefers-reduced-motion` neutralise l'apparition des lignes.
 */
export function Scan({ titre, lignes }: Props) {
  const [visibles, setVisibles] = useState(0);
  const [progression, setProgression] = useState(4);

  useEffect(() => {
    setVisibles(0);
    setProgression(4);

    // Les lignes s'étalent sur l'attente annoncée plutôt que de défiler en trois
    // secondes puis laisser l'écran muet.
    const pas = ATTENTE_ANNONCEE_MS / (lignes.length + 1);
    const timers = lignes.map((_, i) =>
      window.setTimeout(() => setVisibles((v) => Math.max(v, i + 1)), i * pas),
    );
    const barre = window.setInterval(() => {
      setProgression((p) => p + (96 - p) * 0.055);
    }, 600);

    return () => {
      timers.forEach(window.clearTimeout);
      window.clearInterval(barre);
    };
  }, [lignes]);

  return (
    <section>
      {/*
        L'écran d'attente prend toute la hauteur, et non 70 %.

        C'est la plus longue minute du parcours — trente secondes pendant
        lesquelles on demande de ne pas fermer la page. À 70 %, le pied de page
        remontait dans le vide laissé sous les trois lignes : on affichait donc,
        au moment exact où il ne faut pas partir, un plan du site et des liens
        vers ailleurs. Occuper l'écran n'est pas un effet, c'est retirer une
        sortie qu'on n'avait aucune raison de proposer.

        `svh` et non `vh` : sur un téléphone, `vh` compte la hauteur sans les
        barres du navigateur, donc déborde toujours d'une soixantaine de pixels
        et fait apparaître un défilement pour rien.
      */}
      <div className="dark" style={{ minHeight: "calc(100svh - 72px)" }}>
        <div className="shell narrow app">
          {/* Le fil disparaissait ici, c'est-à-dire pendant la plus longue
              attente du parcours — le seul moment où l'on se demande vraiment
              où l'on en est. */}
          <Fil etape={1} />
          <h2 style={{ color: "#fff" }}>
            <span className="pulse" />
            <span>{titre}</span>
          </h2>
          <div
            className="progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progression)}
            aria-label="Avancement de l'analyse"
          >
            <i style={{ width: `${progression}%` }} />
          </div>
          <p className="fine" style={{ marginTop: "12px" }}>
            Comptez une trentaine de secondes : chaque tâche est examinée séparément. Ne fermez
            pas la page.
          </p>
          {/*
            Les trois étapes sont montrées d'emblée, celles à venir en retrait.

            Elles n'apparaissaient qu'une par une, espacées de sept secondes :
            pendant les vingt premières, l'écran ne portait donc qu'une seule
            ligne au-dessus d'un grand vide. Annoncer le chemin entier coûte
            trois lignes grisées et répond à la question qu'on se pose en
            attendant — non pas « est-ce que ça avance », à quoi la barre
            répond, mais « il reste combien ».

            Les étapes non atteintes sont retirées de l'arbre d'accessibilité :
            une liste lue en entier dès la première seconde annoncerait un
            travail déjà fait.
          */}
          <ul className="scanlist">
            {lignes.map((ligne, i) => (
              <li
                className={i < visibles ? "scanline" : "scanline scanline-attente"}
                key={ligne}
                aria-hidden={i < visibles ? undefined : true}
              >
                {ligne}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
